import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import { apiFetch, downloadPdf } from '../../api';
import '../master/Products.css';
import DateTimeText from '../../components/DateTimeText';
import { FaFilter } from 'react-icons/fa';
import Pagination from "../../components/Pagination";

export default function TestCertificateIndex() {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingPdf, setDownloadingPdf] = useState(null);
    // Filter states
  const [showminnoFilter, setShowminnoFilter] = useState(false);
  const [showCustomerFilter, setShowCustomerFilter] = useState(false);
  const [showPONoFilter, setShowPONoFilter] = useState(false);
  const [showSONoFilter, setShowSONoFilter] = useState(false);
  const [minnoFilter, setminnoFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState('');
  const [PONoFilter, setPONoFilter] = useState('');
  const [SONoFilter, setSONoFilter] = useState('');
  const [filteredRows, setFilteredRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const totalItems = useMemo(() => {
    return filteredRows.length;
  }, [filteredRows]);

  const totalPages = useMemo(() => {
    return Math.ceil(totalItems / rowsPerPage);
  }, [totalItems, rowsPerPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1);
  };

  const handlePaginatedUsers = useMemo(() => {
    return filteredRows.slice(
      (currentPage - 1) * rowsPerPage,
      currentPage * rowsPerPage
    );
  }, [filteredRows, currentPage, rowsPerPage]);

  /* =========================
     FETCH TEST CERTIFICATES
     ========================= */
  useEffect(() => {
    fetchTestCertificates();
  }, []);

  const fetchTestCertificates = async () => {
    try {
      const res = await apiFetch('/api/test-certificate/list?per_page=0');

      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
        ? res
        : [];

      setRows(list);
      setFilteredRows(list);
    } catch (err) {
      console.error('Failed to load test certificates', err);
      setRows([]);
      setFilteredRows([]);
    } finally {
      setLoading(false);
    }
  };

    useEffect(() => {
    let filtered = rows;

    if (minnoFilter) {
      filtered = filtered.filter(row =>
        row.certificate_no?.toLowerCase().includes(minnoFilter.toLowerCase())
      );
    }

    if (customerFilter) {
      filtered = filtered.filter(row =>
        row.customer_name?.toLowerCase().includes(customerFilter.toLowerCase())
      );
    }

    if (PONoFilter) {
      filtered = filtered.filter(row =>
        row.po_no?.toLowerCase().includes(PONoFilter.toLowerCase())
      );
    }
    if (SONoFilter) {
      filtered = filtered.filter(row =>
        row.so_no?.toLowerCase().includes(SONoFilter.toLowerCase())
      );
    }

    setFilteredRows(filtered);
  }, [minnoFilter, customerFilter, PONoFilter, SONoFilter, rows]);

  const handleminnoFilterChange = (e) => {
    setminnoFilter(e.target.value);
  };

  const handleCustomerFilterChange = (e) => {
    setCustomerFilter(e.target.value);
  };

  const handlePONoFilterChange = (e) => {
    setPONoFilter(e.target.value);
  };

  const handleSONoFilterChange = (e) => {
    setSONoFilter(e.target.value);
  };


  /* =========================
     DOWNLOAD PDF
     ========================= */
  const handleDownloadPdf = async (id, certificateNo) => {
    try {
      setDownloadingPdf(id);
      
      const blob = await downloadPdf(`/pdf-test-certificate.php?id=${id}`);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Test_Certificate_${certificateNo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('PDF download failed:', error);
      alert(`PDF download failed: ${error.message}`);
    } finally {
      setDownloadingPdf(null);
    }
  };

  /* =========================
     UI
     ========================= */
  if (loading) {
    return (
      <Layout>
        <h2>Loading test certificates...</h2>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* ================= HEADER ================= */}
      <div className="products-toolbar">
        <div>
          <h1 className="products-title">Test Certificates</h1>
          <p className="products-subtitle">
            Manage test certificates for components
          </p>
        </div>

        <div>
          <button
            className="btn-primary"
            onClick={() => navigate('/test-certificate')}
          >
            + Create Test Certificate
          </button>
        </div>
      </div>

      {/* ================= TABLE ================= */}

        <div className="products-table-wrapper">
          <table className="products-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    Certificate No
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: minnoFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowminnoFilter(!showminnoFilter)}
                      title="Filter by Certificate No"
                    />
                  </div>
                  {showminnoFilter && (
                    <input
                      type="text"
                      placeholder="Filter Certificate No..."
                      value={minnoFilter}
                      onChange={handleminnoFilterChange}
                      onBlur={() => setTimeout(() => setShowminnoFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>Test Date</th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    Customer Name
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: customerFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowCustomerFilter(!showCustomerFilter)}
                      title="Filter by Customer Name"
                    />
                  </div>
                  {showCustomerFilter && (
                    <input
                      type="text"
                      placeholder="Filter Customer Name..."
                      value={customerFilter}
                      onChange={handleCustomerFilterChange}
                      onBlur={() => setTimeout(() => setShowCustomerFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    PO No
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: PONoFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowPONoFilter(!showPONoFilter)}
                      title="Filter by PO No"
                    />
                  </div>
                  {showPONoFilter && (
                    <input
                      type="text"
                      placeholder="Filter PO No..."
                      value={PONoFilter}
                      onChange={handlePONoFilterChange}
                      onBlur={() => setTimeout(() => setShowPONoFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                <th>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    SO No
                    <FaFilter
                      style={{ cursor: 'pointer', fontSize: '12px', color: SONoFilter ? '#3b82f6' : '#6b7280' }}
                      onClick={() => setShowSONoFilter(!showSONoFilter)}
                      title="Filter by SO No"
                    />
                  </div>
                  {showSONoFilter && (
                    <input
                      type="text"
                      placeholder="Filter SO No..."
                      value={SONoFilter}
                      onChange={handleSONoFilterChange}
                      onBlur={() => setTimeout(() => setShowSONoFilter(false), 200)}
                      autoFocus
                      style={{
                        marginTop: '8px',
                        padding: '4px 8px',
                        fontSize: '14px',
                        border: '1px solid #d1d5db',
                        borderRadius: '4px',
                        width: '80%',
                        height: '25px'
                      }}
                    />
                  )}
                </th>
                 
                <th>Supply As</th>
                <th>Total Items</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
                {handlePaginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px' }}>
                      No Test Certificate found
                    </td>
                  </tr>
              ) : (
              handlePaginatedUsers?.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>

                  <td>
                    <strong>{row.certificate_no}</strong>
                  </td>

                  <td>
                    <DateTimeText value={row.test_date} />
                  </td>

                  <td>{row.customer_name}</td>

                  <td>{row.po_no || '-'}</td>

                  <td>{row.so_no || '-'}</td>

                  <td>
                    <span style={{
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 12,
                      fontWeight: 600,
                      backgroundColor: row.supply_as === 'gas_train' ? '#dbeafe' : '#fef3c7',
                      color: row.supply_as === 'gas_train' ? '#1e40af' : '#92400e',
                    }}>
                      {row.supply_as === 'gas_train' ? 'Gas Train' : 'Individual'}
                    </span>
                  </td>

                  <td>{row.total_items ?? 0}</td>

                  <td>
                    <span
                      style={{
                        padding: '2px 8px',
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        background:
                          row.status === 'APPROVED'
                            ? '#d1fae5'
                            : row.status === 'REJECTED'
                            ? '#fee2e2'
                            : '#fef3c7',
                        color:
                          row.status === 'APPROVED'
                            ? '#065f46'
                            : row.status === 'REJECTED'
                            ? '#991b1b'
                            : '#92400e',
                      }}
                    >
                      {row.status}
                    </span>
                  </td>

                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        className="btn-link"
                        onClick={() =>
                          navigate(`/test-certificate/${row.id}`)
                        }
                      >
                        View
                      </button>
                      <button
                        className="btn-link"
                        onClick={() => handleDownloadPdf(row.id, row.certificate_no)}
                        disabled={downloadingPdf === row.id}
                        style={{ 
                          color: downloadingPdf === row.id ? '#9ca3af' : '#ffffff',
                          cursor: downloadingPdf === row.id ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {downloadingPdf === row.id ? '⏳ Downloading...' : '📄 PDF'}
                      </button>
                    </div>
                  </td>
                </tr>
               ))
            )}
            </tbody>
          </table>
        </div>
         <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
    </Layout>
  );
}